import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';

/**
 * Copy-to-clipboard row for one credential. Read-only on purpose: these are not editable here.
 *
 * Lifted out of EmployerCreateDialog, where it was inline, once a second dialog needed it: an
 * employer's password can now be reissued as well as issued. Two copies would mean two versions of
 * the clipboard fallback below and two chances for the checkmark timing to drift apart, on the one
 * screen where the user has to trust that what they copied is what they will paste.
 */
const CredentialRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is blocked outside a secure context. The value is on screen and selectable,
      // so a failed copy is a minor inconvenience, not something worth an error dialog.
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 'var(--border-radius-sm)',
        backgroundColor: 'var(--color-surface-subtle)',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: 'var(--color-text-muted)' }} variant="caption">
          {label}
        </Typography>
        <Typography
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
          variant="body2"
        >
          {value}
        </Typography>
      </Box>
      <IconButton
        aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}
        size="small"
        onClick={handleCopy}
      >
        {copied ? (
          <CheckIcon fontSize="small" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
};

export default CredentialRow;
